// src/context/LanguageContext.jsx
import React, { createContext, useState, useContext, useEffect, useRef, useCallback } from 'react';
import API from '../api/config';
import { cacheService } from '../services/cacheService';

// Import translations
import enMessages from '../lang/en/messages';
import ruMessages from '../lang/ru/messages';

const LanguageContext = createContext();

export const useLanguage = () => {
    const context = useContext(LanguageContext);
    if (!context) {
        throw new Error('useLanguage must be used within LanguageProvider');
    }
    return context;
};

// Translation mapping
const translations = {
    en: enMessages,
    ru: ruMessages
};

export const LanguageProvider = ({ children }) => {
    const [language, setLanguage] = useState('en');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [version, setVersion] = useState(0);
    const isFirstLoad = useRef(true);
    const languageLoadAttempted = useRef(false);

    const t = useCallback((key) => {
        const currentTranslations = translations[language] || translations.en;
        return currentTranslations[key] || key;
    }, [language]);

    // Update language state and sync
    const updateLanguage = useCallback((newLang) => {
        if (newLang && (newLang === 'en' || newLang === 'ru')) {
            setLanguage(newLang);
            localStorage.setItem('user_language', newLang);
            cacheService.clear();
            setVersion(prev => prev + 1);

            // Update user object
            const userStr = localStorage.getItem('user');
            if (userStr) {
                try {
                    const userData = JSON.parse(userStr);
                    userData.language_preferences = newLang;
                    localStorage.setItem('user', JSON.stringify(userData));
                    window.dispatchEvent(new CustomEvent('authUserUpdate', {
                        detail: { user: userData }
                    }));
                } catch (e) {
                    console.error('Failed to update user object:', e);
                }
            }
        }
    }, []);

    // Load language from API or localStorage
    useEffect(() => {
        if (languageLoadAttempted.current) return;
        languageLoadAttempted.current = true;

        const loadUserLanguage = async () => {
            try {
                const token = localStorage.getItem('access_token') || localStorage.getItem('token');

                if (token) {
                    const response = await API.get('get-language', {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Accept': 'application/json'
                        }
                    });

                    if (response.data && response.data.status === 'success') {
                        const lang = response.data.language || 'en';
                        updateLanguage(lang);
                        setLoading(false);
                        return;
                    }
                }

                // Fallback to localStorage
                const savedLang = localStorage.getItem('user_language');
                if (savedLang && (savedLang === 'en' || savedLang === 'ru')) {
                    setLanguage(savedLang);
                }
            } catch (error) {
                const savedLang = localStorage.getItem('user_language');
                if (savedLang && (savedLang === 'en' || savedLang === 'ru')) {
                    setLanguage(savedLang);
                }
            } finally {
                setLoading(false);
            }
        };

        loadUserLanguage();

        const handleLanguageChange = (event) => {
            const newLang = event.detail?.language;
            if (newLang && (newLang === 'en' || newLang === 'ru')) {
                updateLanguage(newLang);
            }
        };

        window.addEventListener('languageChanged', handleLanguageChange);

        return () => {
            window.removeEventListener('languageChanged', handleLanguageChange);
        };
    }, [updateLanguage]);

    // Change language
    const changeLanguage = useCallback(async (newLanguage) => {
        if (!newLanguage || (newLanguage !== 'en' && newLanguage !== 'ru')) {
            return false;
        }

        if (newLanguage === language) {
            return true;
        }

        try {
            // Optimistic update
            updateLanguage(newLanguage);
            setError(null);

            const token = localStorage.getItem('access_token') || localStorage.getItem('token');

            if (token) {
                await API.post('set-language',
                    { language: newLanguage },
                    {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json'
                        }
                    }
                );
            }

            return true;
        } catch (error) {
            console.error('Error changing language:', error);
            setError(error.response?.data?.message || 'Failed to change language');
            // Revert
            const prevLang = language;
            setLanguage(prevLang);
            localStorage.setItem('user_language', prevLang);
            return false;
        }
    }, [language, updateLanguage]);

    const value = {
        language,
        changeLanguage,
        t,
        loading,
        error,
        updateLanguage,
        version
    };

    return (
        <LanguageContext.Provider value={value}>
            <React.Fragment key={version}>
                {children}
            </React.Fragment>
        </LanguageContext.Provider>
    );
};

export default LanguageContext;